import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, SectionTag, MaskedLine, TiltCard } from "./Motion";

const products = [
  {
    name: "BK Titan Card",
    tag: "Membership Hardware",
    price: "₹4,999",
    img: "https://images.unsplash.com/photo-1572798793517-03198986ca9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBibGFjayUyMGdvbGQlMjBkYXJrJTIwYWJzdHJhY3QlMjBnb2xkJTIwcHJvZHVjdHMlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4OTExOTI1MXww&ixlib=rb-4.1.0&q=85",
    testid: "product-card-1",
  },
  {
    name: "Studio Pulse Kit",
    tag: "Creator Gear",
    price: "₹18,499",
    img: "https://images.unsplash.com/photo-1504610926078-a1611febcad3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0ZWNoJTIwYmxhY2slMjBnb2xkJTIwcHJvZHVjdHMlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4OTExOTI1MXww&ixlib=rb-4.1.0&q=85",
    testid: "product-card-2",
  },
  {
    name: "Command Slate",
    tag: "Analytics Console",
    price: "₹32,999",
    img: "https://images.unsplash.com/photo-1545639599-e049a5b6478a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB0ZWNoJTIwYmxhY2slMjBnb2xkJTIwcHJvZHVjdHMlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4OTExOTI1MXww&ixlib=rb-4.1.0&q=85",
    testid: "product-card-3",
  },
];

const ProductCard = ({ p, i }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
    >
      <TiltCard intensity={8} className="h-full" testid={p.testid}>
        <div
          ref={ref}
          onMouseMove={onMove}
          className="group relative h-full overflow-hidden rounded-2xl border border-gold/15 bg-panel/80 backdrop-blur-xl transition-colors duration-500 hover:border-gold/45"
        >
          <div
            className="pointer-events-none absolute inset-0 z-20 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{
              background:
                "radial-gradient(320px circle at var(--mx, 50%) var(--my, 50%), rgb(var(--gold-light) / 0.14), transparent 70%)",
            }}
          />
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={p.img}
              alt={p.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            <span className="absolute left-5 top-5 rounded-full border border-gold/30 bg-black/40 px-3 py-1 font-mono text-[9px] tracking-[0.25em] text-goldlight backdrop-blur-md">
              {p.tag.toUpperCase()}
            </span>
          </div>
          <div className="relative z-10 flex items-end justify-between p-6 sm:p-7">
            <div>
              <h3 className="text-xl font-semibold text-neutral-50 sm:text-2xl">{p.name}</h3>
              <div className="mt-2 font-serif text-lg italic text-gold-grad">{p.price}</div>
            </div>
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-black">
              <ArrowUpRight size={18} />
            </span>
          </div>
        </div>
      </TiltCard>
    </motion.div>
  );
};

export default function Products() {
  return (
    <section id="products" data-testid="products-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <SectionTag index="08" label="The Vault" testid="products-tag" />
        <h2 className="mt-6 max-w-3xl text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
          <MaskedLine>Sourced for</MaskedLine>
          <MaskedLine delay={0.12} innerClassName="font-serif italic font-medium text-gold-grad">
            the spotlight.
          </MaskedLine>
        </h2>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-neutral-400">
          A rotating vault of hardware and creator gear, hand-picked by our sourcing desk and shot
          under studio light — because presentation is half the product.
        </p>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {products.map((p, i) => (
            <ProductCard key={p.name} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
